import { forwardRef, useId } from "react";
import type { SelectHTMLAttributes } from "react";
import { cn } from "../lib/cn";

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label: string;
  options: SelectOption[];
  hint?: string;
  error?: string;
  placeholder?: string;
  hideLabel?: boolean;
}

/**
 * Native <select> styled to match the system, with a custom chevron. Native
 * is deliberate: it gives correct mobile pickers and full keyboard / a11y
 * behaviour for free while still looking on-brand.
 */
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    { label, options, hint, error, placeholder, hideLabel, id, className, required, ...rest },
    ref,
  ) => {
    const auto = useId();
    const fieldId = id ?? auto;
    const hintId = hint ? `${fieldId}-hint` : undefined;
    const errId = error ? `${fieldId}-err` : undefined;

    return (
      <div className="w-full">
        <label
          htmlFor={fieldId}
          className={cn(
            "mb-1.5 block font-display text-sm font-bold text-body",
            hideLabel && "sr-only",
          )}
        >
          {label}
          {required && <span className="ml-1 text-ember">*</span>}
        </label>

        <div className="relative">
          <select
            ref={ref}
            id={fieldId}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={cn(hintId, errId) || undefined}
            defaultValue={placeholder ? "" : undefined}
            className={cn(
              "h-12 w-full appearance-none rounded-md bg-surface pl-4 pr-10 text-base text-body",
              "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast",
              "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-60",
              error && "ring-2 ring-danger focus:ring-danger",
              className,
            )}
            {...rest}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <svg
            aria-hidden
            viewBox="0 0 20 20"
            className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-body-muted"
            fill="none"
          >
            <path
              d="M5 7.5 10 12.5 15 7.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {error ? (
          <p id={errId} className="mt-1.5 text-sm font-medium text-danger">
            {error}
          </p>
        ) : (
          hint && (
            <p id={hintId} className="mt-1.5 text-sm text-body-muted">
              {hint}
            </p>
          )
        )}
      </div>
    );
  },
);
SelectField.displayName = "SelectField";
